import React, { useState, useEffect } from "react";
import {
  Drawer,
  Box,
  IconButton,
  Stack,
  Typography,
  Button,
  Avatar,
  useMediaQuery,
} from "@mui/material";
import { Link, useNavigate } from "react-router-dom";
import { FiMenu } from "react-icons/fi";
import { useDispatch, useSelector } from "react-redux";
import { logoutUserAction } from "../../../store/slices/authSlice";
import { Images } from "../../../utils/Images";
import AuthDialog from "../../common/AuthDialog";

const MobileMenu = ({ menuItems, activeMenuItem, handleMenuItemClick }) => {
  const [open, setOpen] = React.useState(false);
  const [authDialogOpen, setAuthDialogOpen] = useState(false);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { isAuthenticated, user } = useSelector((state) => state.auth);
  
  // Check if screen is below 600px (mobile)
  const isMobile = useMediaQuery('(max-width:599px)');

  // Handle responsive transitions - close popup when going to mobile
  useEffect(() => {
    const handleResize = () => {
      const currentWidth = window.innerWidth;
      const isCurrentlyMobile = currentWidth < 600;
      
      // If screen becomes mobile (below 600px) while popup is open, close it
      if (isCurrentlyMobile && authDialogOpen) {
        setAuthDialogOpen(false);
      }
    };

    // Add resize listener
    window.addEventListener('resize', handleResize);
    
    // Cleanup
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [authDialogOpen]);

  const handleMenu = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const onMenuItemClick = (label) => {
    handleMenuItemClick(label);
    handleClose();
  };

  const handleLoginClick = () => {
    handleClose();
    
    // If mobile (below 600px), navigate to login page
    if (isMobile) {
      navigate("/login");
    } else {
      // If tablet/desktop (600px and above), show popup
      setAuthDialogOpen(true);
    }
  };

  const handleAuthDialogClose = () => {
    setAuthDialogOpen(false);
  };

  const handleLogout = async () => {
    try {
      await dispatch(logoutUserAction());
      handleClose();
      navigate("/");
    } catch (error) {
      console.error("Logout failed:", error);
      // Even if logout fails on server, clear local state and redirect to home
      localStorage.removeItem("authToken");
      navigate("/");
    }
  };

  // Map icons to menu items
  const getMenuIcon = (label) => {
    switch (label) {
      case "Home":
        return (
          <Box
            component="img"
            src={Images.homeMIcon}
            alt="Home"
            width={20}
            height={20}
          />
        );
      case "Life":
        return (
          <Box
            component="img"
            src={Images.healthInsuranceMIcon}
            alt="Life"
            width={20}
            height={20}
          />
        );
      case "Explore Campaigns":
        return (
          <Box
            component="img"
            src={Images.exploreMIcon}
            alt="Explore Campaigns"
            width={20}
            height={20}
          />
        );
      case "Monthly Donation":
        return (
          <Box
            component="img"
            src={Images.heartMIcon}
            alt="Monthly Donation"
            width={20}
            height={20}
          />
        );
      case "Small Vendors":
        return (
          <Box
            component="img"
            src={Images.shop}
            alt="Small Vendors"
            width={20}
            height={20}
          />
        );
      case "Temples":
        return (
          <Box
            component="img"
            src={Images.temples}
            alt="Temples"
            width={20}
            height={20}
          />
        );
      case "Start New Campaign":
        return (
          <Box
            component="img"
            src={Images.rocketMIcon}
            alt="Start New Campaign"
            width={20}
            height={20}
          />
        );
      case "Blogs":
        return (
          <Box
            component="img"
            src={Images.subtitleMIcon}
            alt="Blogs"
            width={20}
            height={20}
          />
        );
      case "Contact Us":
        return (
          <Box
            component="img"
            src={Images.callMIcon}
            alt="Contact Us"
            width={20}
            height={20}
          />
        );
      case "About Us":
        return (
          <Box
            component="img"
            src={Images.infoCircleMIcon}
            alt="About Us"
            width={20}
            height={20}
          />
        );
      default:
        return null;
    }
  };

     return (
     <>
       {/* Auth Dialog for tablet/desktop (600px and above) */}
       <AuthDialog 
         open={authDialogOpen} 
         onClose={handleAuthDialogClose} 
         initialView="login"
       />
       
       <IconButton
        size="large"
        edge="start"
        color="inherit"
        aria-label="menu"
        onClick={handleMenu}
        sx={{ color: "#151515" }}
      >
        <FiMenu />
      </IconButton>
      <Drawer
        anchor="right"
        open={open}
        onClose={handleClose}
        PaperProps={{
          sx: {
            width: "100%",
            maxWidth: "270px",
            padding: "10px",
            borderTopLeftRadius: "20px",
            display: "flex",
            flexDirection: "column",
            // bgcolor: "#f8f9f3",
          },
        }}
      >
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mb={2}
          p={"8px"}
        >
          <Box
            component={Link}
            to="/"
            sx={{
              textDecoration: "none",
              display: "block",
            }}
            onClick={handleClose}
          >
            <Box
              component="img"
              src={Images.logo}
              alt="Logo"
              sx={{
                width: "119px",
                height: "33px",
                objectFit: "contain",
                imageRendering: "auto",
                WebkitImageRendering: "-webkit-optimize-contrast",
                transform: "translateZ(0)",
                backfaceVisibility: "hidden",
                WebkitBackfaceVisibility: "hidden",
                willChange: "transform",
                WebkitFontSmoothing: "antialiased",
                MozOsxFontSmoothing: "grayscale",
                WebkitTransform: "translateZ(0)",
              }}
            />
          </Box>
          <IconButton
            onClick={handleClose}
            sx={{ width: "20px", height: "20px", p: "0px" }}
          >
            <Box
              component="img"
              src={Images.removeMIcon}
              alt="Close"
              width={20}
              height={20}
            />
          </IconButton>
        </Box>

        <Stack
          spacing={"12px"}
          sx={{
            "& a": {
              outline: "none !important",
              textDecoration: "none !important",
              WebkitTapHighlightColor: "transparent",
            },
          }}
        >
          {menuItems.map((item) => (
            <Box
              key={item.label}
              component={Link}
              to={item.path}
              sx={{
                textDecoration: "none",
                display: "flex",
                alignItems: "center",
                padding: "12px",
                borderRadius: "8px",
                backgroundColor:
                  activeMenuItem === item.label ? "#F5F8ED" : "transparent",
                outline: "none",
                "&:hover": {
                  backgroundColor: "#F5F8ED",
                },
                "&:focus": {
                  outline: "none",
                  textDecoration: "none",
                },
              }}
              onClick={() => onMenuItemClick(item.label)}
            >
              <Box
                sx={{
                  filter:
                    activeMenuItem === item.label ? "brightness(0)" : "none",
                  mr: "16px",
                }}
              >
                {getMenuIcon(item.label)}
              </Box>
              <Typography
                variant="body1"
                sx={{
                  // fontFamily: "Red Hat Display",
                  fontWeight: 500,
                  fontStyle: "normal",
                  fontSize: "14px",
                  lineHeight: "20px",
                  letterSpacing: "0%",
                  color: activeMenuItem === item.label ? "#1D1D1B" : "#4A4A49",
                }}
              >
                {item.label}
              </Typography>
            </Box>
          ))}
        </Stack>

        <Box sx={{ marginTop: "auto" }}>
          {isAuthenticated && user ? (
            <Box>
              {/* User Profile */}
              <Box 
                sx={{ 
                  display: "flex", 
                  alignItems: "center", 
                  mb: "12px",
                  cursor: "pointer",
                  p: "8px",
                  borderRadius: "8px",
                  "&:hover": {
                    backgroundColor: "#F5F8ED",
                  },
                }}
                onClick={() => {
                  handleClose();
                  navigate("/profile");
                }}
              >
                <Avatar 
                  src={user.profilePicture || `https://ui-avatars.com/api/?name=${user.name}&background=B3B3B3&color=fff&size=40`}
                  alt={user.name}
                  sx={{ 
                    width: 40, 
                    height: 40,
                    mr: "12px"
                  }}
                >
                  {user.name ? user.name.charAt(0).toUpperCase() : "U"}
                </Avatar>
                <Box>
                  <Typography
                    sx={{
                      // fontFamily: "Red Hat Display",
                      fontWeight: 700,
                      fontStyle: "normal",
                      fontSize: "14px",
                      lineHeight: "100%",
                      letterSpacing: "0%",
                      verticalAlign: "middle",
                      color: "#1D1D1B",
                      mb: "2px"
                    }}
                  >
                    {user.name}
                  </Typography>
                  <Typography
                    sx={{
                      // fontFamily: "Red Hat Display",
                      fontWeight: 500,
                      fontStyle: "normal",
                      fontSize: "12px",
                      lineHeight: "18px",
                      letterSpacing: "0%",
                      verticalAlign: "middle",
                      color: "#686866"
                    }}
                  >
                    {user.email}
                  </Typography>
                </Box>
              </Box>
              
              {/* Logout Button */}
              <Button
                fullWidth
                variant="contained"
                disableRipple
                onClick={handleLogout}
                sx={{
                  backgroundColor: "transparent",
                  color: "#4A4A49 !important",
                  padding: "12px",
                  borderRadius: "4px",
                  // fontFamily: "Red Hat Display",
                  fontWeight: 500,
                  fontStyle: "normal",
                  fontSize: "14px",
                  lineHeight: "20px",
                  letterSpacing: "0%",
                  textTransform: "none",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "flex-start",
                  gap: "16px",
                  boxShadow: "none",
                  textDecoration: "none !important",
                  "& .MuiButton-label": {
                    color: "#4A4A49 !important"
                  },
                  "&:hover": {
                    backgroundColor: "transparent",
                    boxShadow: "none",
                    color: "#4A4A49 !important",
                    textDecoration: "none !important",
                  },
                  "&:focus": {
                    outline: "none",
                    boxShadow: "none",
                    color: "#4A4A49 !important",
                  },
                  "&:active": {
                    boxShadow: "none",
                    color: "#4A4A49 !important",
                  },
                  "&:visited": {
                    color: "#4A4A49 !important",
                  },
                }}
                startIcon={
                  <Box
                    component="img"
                    src={Images.logoutMIcon}
                    alt="Logout"
                    width={20}
                    height={20}
                  />
                }
              >
                <Typography
                  component="span"
                  sx={{
                    color: "#4A4A49 !important",
                    // fontFamily: "Red Hat Display",
                    fontWeight: 500,
                    fontStyle: "normal",
                    fontSize: "14px",
                    lineHeight: "20px",
                    letterSpacing: "0%",
                    "&:hover": { color: "#4A4A49 !important" },
                  }}
                >
                  Logout
                </Typography>
              </Button>
            </Box>
          ) : (
            <Button
              fullWidth
              variant="contained"
              disableRipple
              onClick={handleLoginClick}
              sx={{
                backgroundColor: "#1D1D1B",
                color: "#FFFFFF !important", // Force white text
                padding: "12px",
                borderRadius: "4px",
                // fontFamily: "Red Hat Display",
                fontWeight: 500,
                fontStyle: "normal",
                fontSize: "14px",
                lineHeight: "20px",
                letterSpacing: "0%",
                textTransform: "none",
                display: "flex",
                alignItems: "center",
                justifyContent: "flex-start",
                gap: "16px",
                boxShadow: "none",
                textDecoration: "none !important", // Prevent text decoration
                "& .MuiButton-label": {
                  color: "#FFFFFF !important", // Force white text on inner label
                },
                "&:hover": {
                  backgroundColor: "#000000",
                  boxShadow: "none",
                  color: "#FFFFFF !important", // Force white text on hover
                  textDecoration: "none !important", // Prevent text decoration on hover
                },
                "&:focus": {
                  outline: "none",
                  boxShadow: "none",
                  color: "#FFFFFF !important", // Force white text on focus
                },
                "&:active": {
                  boxShadow: "none",
                  color: "#FFFFFF !important", // Force white text on active
                },
                "&:visited": {
                  color: "#FFFFFF !important", // Force white text on visited
                },
              }}
              startIcon={
                <Box
                  component="img"
                  src={Images.loginMIcon}
                  alt="Login"
                  width={20}
                  height={20}
                />
              }
            >
              <Typography
                component="span"
                sx={{
                  color: "#FFFFFF !important",
                  // fontFamily: "Red Hat Display",
                  fontWeight: 500,
                  fontStyle: "normal",
                  fontSize: "14px",
                  lineHeight: "20px",
                  letterSpacing: "0%",
                  "&:hover": { color: "#FFFFFF !important" },
                }}
              >
                Login/Register
              </Typography>
            </Button>
          )}
        </Box>
      </Drawer>
      

    </>
  );
};

export default MobileMenu;
